import type { CSSProperties, ReactNode } from "react";

import { useTranslation } from "react-i18next";

import {
  CircleCheckIcon,
  KeyIcon,
  LoaderCircleIcon,
  MailIcon,
  SmartphoneIcon,
} from "lucide-react";

import type { MfaMethod } from "simplydo/core/mfa";
import { Button } from "simplydo/react/ui/button";
import { Icon, IconData } from "simplydo/react/ui/icon";

const MFA_METHOD_ICONS: Record<MfaMethod, IconData> = {
  totp: SmartphoneIcon,
  emailOtp: MailIcon,
  passkey: KeyIcon,
};

const cardShellStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  textAlign: "left",
  padding: "16px 18px",
  marginBottom: 12,
  border: "1px solid rgba(34,36,38,.15)",
  borderRadius: 6,
  background: "#fff",
  width: "100%",
};

type MfaMethodCardProps = {
  method: MfaMethod;
  onSelect?: () => void;
  disabled?: boolean;
  loading?: boolean;
  configured?: boolean;
  actions?: ReactNode;
  children?: ReactNode;
};

export const MfaMethodCard = ({
  method,
  onSelect,
  disabled = false,
  loading = false,
  configured = false,
  actions,
  children,
}: MfaMethodCardProps) => {
  const { t } = useTranslation();
  const selectable = Boolean(onSelect) && !disabled;

  const body = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: configured ? "rgba(33,186,69,0.1)" : "rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            lineHeight: 0,
          }}
        >
          <Icon
            icon={loading ? LoaderCircleIcon : MFA_METHOD_ICONS[method]}
            style={{
              margin: 0,
              fontSize: "1.2em",
              lineHeight: 1,
              display: "block",
              color: configured ? "#21ba45" : "rgba(0,0,0,0.55)",
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "1.05em",
                color: "rgba(0,0,0,0.87)",
              }}
            >
              {t(`entry.mfa.method.${method}`)}
            </div>
            {configured ? (
              <span
                style={{
                  color: "#21ba45",
                  fontSize: "0.85em",
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  flexShrink: 0,
                  padding: "4px 10px",
                  borderRadius: 12,
                  background: "rgba(33,186,69,0.1)",
                }}
              >
                <Icon icon={CircleCheckIcon} />
                <span style={{ lineHeight: 1 }}>
                  {t("entry.mfa.methodEnabled")}
                </span>
              </span>
            ) : null}
          </div>
          <div
            style={{
              fontWeight: 400,
              fontSize: "0.92em",
              lineHeight: 1.45,
              color: "rgba(0,0,0,0.55)",
              marginTop: 6,
            }}
          >
            {t(`entry.mfa.methodDescription.${method}`)}
          </div>
        </div>
      </div>
      {children ? (
        <div style={{ marginTop: 14, marginLeft: 54 }}>{children}</div>
      ) : null}
      {actions ? (
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: "1px solid rgba(34,36,38,.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          {actions}
        </div>
      ) : null}
    </>
  );

  if (selectable) {
    return (
      <Button
        fluid
        style={{
          ...cardShellStyle,
          height: "auto",
          opacity: disabled ? 0.65 : 1,
          boxShadow: "none",
          alignItems: "flex-start",
        }}
        onClick={onSelect}
        disabled={disabled || loading}
        variant="outline"
      >
        {body}
      </Button>
    );
  }

  return (
    <div style={{ ...cardShellStyle, opacity: disabled ? 0.65 : 1 }}>
      {body}
    </div>
  );
};
